import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminProductsApi } from '../api/catalog';
import { adminInventoryApi } from '../api/operations';
import { adminOrdersApi } from '../api/sales';
import { adminShippingLocationsApi } from '../api';
import type { AddressInputDto, CreateManualOrderDto, ManualOrderLineDto } from '../api/types';
import { PageHeader } from '../components/Shell';
import { Field, FormErrors, Spinner } from '../components/ui';
import { SearchableSelect } from '../components/SearchableSelect';
import { useToast } from '../components/Toast';
import { money } from '../lib/format';
import { TURKEY_CITIES, getDistrictsForCity } from '../lib/turkeyLocations';

/* ------------------------------------------------------------------ *
 *  Panelden manuel siparis (telefon siparisi, kurumsal talep).
 *
 *  Taslak kaydedilirse odeme beklenmez; "odeme baglantisi gonder"
 *  secilirse musteriye e-posta ile odeme bagi iletilir.
 * ------------------------------------------------------------------ */

const emptyAddress: AddressInputDto = {
  firstName: '',
  lastName: '',
  phone: '',
  addressLine1: '',
  district: '',
  city: '',
  countryCode: 'TR',
  isCorporate: false,
};

export function ManualOrderPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState<CreateManualOrderDto>({
    email: '',
    phone: '',
    lines: [],
    shippingAddress: emptyAddress,
    discountCodes: [],
    saveAsDraft: true,
    sendPaymentLink: false,
  });
  const [search, setSearch] = useState('');

  // 1. Fetch cities from unified backend API (with fallback to TURKEY_CITIES)
  const { data: apiCities } = useQuery({
    queryKey: ['shipping', 'cities'],
    queryFn: adminShippingLocationsApi.getCities,
    staleTime: 1000 * 60 * 60,
  });
  const cities = useMemo(() => {
    if (apiCities && apiCities.length > 0) return apiCities;
    return TURKEY_CITIES;
  }, [apiCities]);

  const selectedCityObj = useMemo(() => {
    return cities.find((c) => c.name === form.shippingAddress.city);
  }, [cities, form.shippingAddress.city]);

  // 2. Fetch districts from unified backend API for selected city
  const { data: apiDistricts } = useQuery({
    queryKey: ['shipping', 'districts', selectedCityObj?.id],
    queryFn: () => adminShippingLocationsApi.getDistricts(selectedCityObj!.id),
    enabled: !!selectedCityObj?.id,
    staleTime: 1000 * 60 * 60,
  });
  const districts = useMemo(() => {
    if (apiDistricts && apiDistricts.length > 0) {
      return apiDistricts.map((d) => d.name);
    }
    return getDistrictsForCity(form.shippingAddress.city);
  }, [apiDistricts, form.shippingAddress.city]);

  const [neighborhoods, setNeighborhoods] = useState<{ value: string; label: string }[]>([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  useEffect(() => {
    if (!selectedCityObj || !form.shippingAddress.district) {
      setNeighborhoods([]);
      return;
    }

    let isMounted = true;
    setLoadingNeighborhoods(true);
    adminShippingLocationsApi
      .getNeighborhoods(selectedCityObj.id, form.shippingAddress.district)
      .then((items) => {
        if (!isMounted) return;
        setNeighborhoods(items.map((n) => ({ value: n.name, label: n.fullName || n.name })));
      })
      .catch(() => {
        if (!isMounted) return;
        setNeighborhoods([]);
      })
      .finally(() => {
        if (isMounted) setLoadingNeighborhoods(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedCityObj?.id, form.shippingAddress.district]);

  // Urun arama: yalnizca 2 karakterden sonra sorgu yapilir.
  const products = useQuery({
    queryKey: ['admin', 'products', 'manual-search', search],
    queryFn: () => adminProductsApi.search({ search, pageSize: 10, status: 'Active' }),
    enabled: search.trim().length >= 2,
  });

  const create = useMutation({
    mutationFn: () => adminOrdersApi.createManual(form),
    onSuccess: (order) => {
      toast.success(`Sipariş oluşturuldu: ${order.orderNumber}`);
      navigate(`/siparisler/${order.id}`, { replace: true });
    },
  });

  const addLine = (productVariantId: number) => {
    setForm((current) => {
      const existing = current.lines.find((line) => line.productVariantId === productVariantId);

      return {
        ...current,
        lines: existing
          ? current.lines.map((line) =>
              line.productVariantId === productVariantId
                ? { ...line, quantity: line.quantity + 1 }
                : line,
            )
          : [...current.lines, { productVariantId, quantity: 1 } satisfies ManualOrderLineDto],
      };
    });
  };

  const updateLine = (index: number, patch: Partial<ManualOrderLineDto>) =>
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    }));

  const setAddress = <K extends keyof AddressInputDto>(key: K, value: AddressInputDto[K]) =>
    setForm((current) => ({
      ...current,
      shippingAddress: { ...current.shippingAddress, [key]: value },
    }));

  const canSubmit =
    form.email.length > 0 &&
    form.lines.length > 0 &&
    form.shippingAddress.firstName.length > 0 &&
    form.shippingAddress.lastName.length > 0 &&
    form.shippingAddress.phone.length > 0 &&
    form.shippingAddress.addressLine1.length > 0 &&
    form.shippingAddress.city.length > 0;

  return (
    <div>
      <PageHeader
        title="Manuel sipariş"
        description="Telefon veya kurumsal talepler için panelden sipariş oluşturun."
        actions={
          <>
            <Link to="/siparisler" className="btn-secondary">
              Listeye dön
            </Link>
            <button
              type="button"
              className="btn-primary"
              onClick={() => create.mutate()}
              disabled={!canSubmit || create.isPending}
            >
              {create.isPending ? <Spinner className="h-4 w-4" /> : null}
              Siparişi oluştur
            </button>
          </>
        }
      />

      <div className="mb-5">
        <FormErrors error={create.error} />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5">
          {/* Urun secimi */}
          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Ürünler</h2>

            <Field label="Ürün ara" htmlFor="mo-search" hint="En az 2 karakter yazın.">
              <input
                id="mo-search"
                type="search"
                className="field"
                placeholder="Ürün adı veya SKU"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Field>

            {products.data && products.data.items.length > 0 ? (
              <ul className="mt-3 divide-y divide-ink-100 rounded-lg border border-ink-200">
                {products.data.items.map((product) => (
                  <li key={product.id} className="flex items-center gap-3 p-3">
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-ink-50">
                      {product.primaryImageUrl ? (
                        <img
                          src={product.primaryImageUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-ink-900">{product.title}</p>
                      <p className="truncate text-xs text-ink-400">
                        {product.variantCount} varyant · {money(product.minPrice)} · stok{' '}
                        {product.totalStock}
                      </p>
                    </div>
                    <VariantPicker productId={product.id} onPick={addLine} />
                  </li>
                ))}
              </ul>
            ) : search.trim().length >= 2 && !products.isFetching ? (
              <p className="mt-3 text-sm text-ink-500">Eşleşen ürün yok.</p>
            ) : null}

            {form.lines.length > 0 ? (
              <div className="scroll-x mt-5">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                      <th className="pb-2 font-semibold">Varyant</th>
                      <th className="pb-2 text-right font-semibold">Adet</th>
                      <th className="pb-2 text-right font-semibold">Özel fiyat</th>
                      <th className="pb-2 text-right font-semibold">Elle indirim</th>
                      <th className="pb-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {form.lines.map((line, index) => (
                      <tr key={line.productVariantId}>
                        <td className="py-2 font-mono text-xs text-ink-700">
                          #{line.productVariantId}
                        </td>
                        <td className="py-2">
                          <input
                            type="number"
                            min={1}
                            className="field w-20 py-1.5 text-right text-sm"
                            value={line.quantity}
                            aria-label="Adet"
                            onChange={(event) =>
                              updateLine(index, { quantity: Number(event.target.value) })
                            }
                          />
                        </td>
                        <td className="py-2">
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            className="field w-24 py-1.5 text-right text-sm"
                            placeholder="varsayılan"
                            aria-label="Özel fiyat"
                            value={line.priceOverride ?? ''}
                            onChange={(event) =>
                              updateLine(index, {
                                priceOverride: event.target.value
                                  ? Number(event.target.value)
                                  : null,
                              })
                            }
                          />
                        </td>
                        <td className="py-2">
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            className="field w-24 py-1.5 text-right text-sm"
                            aria-label="Elle indirim"
                            value={line.manualDiscount ?? ''}
                            onChange={(event) =>
                              updateLine(index, {
                                manualDiscount: event.target.value
                                  ? Number(event.target.value)
                                  : null,
                              })
                            }
                          />
                        </td>
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            className="btn-ghost btn-sm text-red-600"
                            onClick={() =>
                              setForm((current) => ({
                                ...current,
                                lines: current.lines.filter((_, i) => i !== index),
                              }))
                            }
                          >
                            Kaldır
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-5 rounded-lg border border-dashed border-ink-200 px-4 py-8 text-center text-sm text-ink-500">
                Henüz ürün eklenmedi.
              </p>
            )}
          </section>

          {/* Teslimat adresi */}
          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Teslimat adresi</h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Ad" htmlFor="mo-first" required>
                <input
                  id="mo-first"
                  className="field"
                  value={form.shippingAddress.firstName}
                  onChange={(event) => setAddress('firstName', event.target.value)}
                />
              </Field>

              <Field label="Soyad" htmlFor="mo-last" required>
                <input
                  id="mo-last"
                  className="field"
                  value={form.shippingAddress.lastName}
                  onChange={(event) => setAddress('lastName', event.target.value)}
                />
              </Field>

              <Field label="Telefon" htmlFor="mo-phone" required>
                <input
                  id="mo-phone"
                  type="tel"
                  className="field"
                  value={form.shippingAddress.phone}
                  onChange={(event) => setAddress('phone', event.target.value)}
                />
              </Field>

              <Field label="Şirket" htmlFor="mo-company">
                <input
                  id="mo-company"
                  className="field"
                  value={form.shippingAddress.company ?? ''}
                  onChange={(event) => setAddress('company', event.target.value)}
                />
              </Field>

              <Field label="İl" htmlFor="mo-city" required>
                <SearchableSelect
                  id="mo-city"
                  value={form.shippingAddress.city}
                  placeholder="İl Seçiniz..."
                  searchPlaceholder="İl adı veya plaka ara (örn: 34 veya İstanbul)..."
                  options={cities.map((city) => ({
                    value: city.name,
                    label: `${city.plate} - ${city.name}`,
                  }))}
                  onChange={(newCity) => {
                    setForm((c) => ({
                      ...c,
                      shippingAddress: {
                        ...c.shippingAddress,
                        city: newCity,
                        district: '',
                        addressLine2: '',
                      },
                    }));
                  }}
                />
              </Field>

              <Field label="İlçe" htmlFor="mo-district" required>
                <SearchableSelect
                  id="mo-district"
                  value={form.shippingAddress.district}
                  placeholder="İlçe Seçiniz..."
                  searchPlaceholder="İlçe ara (örn: Kadıköy)..."
                  options={districts.map((d) => ({
                    value: d,
                    label: d,
                  }))}
                  onChange={(newDistrict) =>
                    setForm((c) => ({
                      ...c,
                      shippingAddress: {
                        ...c.shippingAddress,
                        district: newDistrict,
                        addressLine2: '',
                      },
                    }))
                  }
                  disabled={!form.shippingAddress.city}
                />
              </Field>

              <Field label="Mahalle" htmlFor="mo-neighborhood">
                {neighborhoods.length > 0 ? (
                  <SearchableSelect
                    id="mo-neighborhood"
                    value={form.shippingAddress.addressLine2 ?? ''}
                    placeholder="Mahalle Seçiniz..."
                    searchPlaceholder="Mahalle ara..."
                    isLoading={loadingNeighborhoods}
                    options={neighborhoods}
                    onChange={(newNeighborhood) => setAddress('addressLine2', newNeighborhood)}
                    disabled={!form.shippingAddress.district}
                  />
                ) : (
                  <input
                    id="mo-line2"
                    className="field"
                    placeholder="Mahalle veya ek adres bilgisi"
                    value={form.shippingAddress.addressLine2 ?? ''}
                    onChange={(event) => setAddress('addressLine2', event.target.value)}
                  />
                )}
              </Field>

              <Field label="Posta kodu" htmlFor="mo-postal">
                <input
                  id="mo-postal"
                  className="field"
                  placeholder="Örn: 34710"
                  value={form.shippingAddress.postalCode ?? ''}
                  onChange={(event) => setAddress('postalCode', event.target.value)}
                />
              </Field>

              <Field label="Açık Adres (Cadde, Sokak, Kapı No, Daire)" htmlFor="mo-line1" required className="sm:col-span-2">
                <textarea
                  id="mo-line1"
                  rows={2}
                  className="field"
                  placeholder="Örn: İstiklal Cad. Çiçek Pasajı No: 12 Daire: 4"
                  value={form.shippingAddress.addressLine1}
                  onChange={(event) => setAddress('addressLine1', event.target.value)}
                />
              </Field>
            </div>
          </section>
        </div>

        {/* Yan panel */}
        <aside className="space-y-5">
          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Müşteri</h2>

            <div className="space-y-4">
              <Field label="E-posta" htmlFor="mo-email" required>
                <input
                  id="mo-email"
                  type="email"
                  className="field"
                  value={form.email}
                  onChange={(event) => setForm((c) => ({ ...c, email: event.target.value }))}
                />
              </Field>

              <Field label="Telefon" htmlFor="mo-cphone">
                <input
                  id="mo-cphone"
                  type="tel"
                  className="field"
                  value={form.phone ?? ''}
                  onChange={(event) => setForm((c) => ({ ...c, phone: event.target.value }))}
                />
              </Field>

              <Field
                label="Kayıtlı müşteri kimliği"
                htmlFor="mo-userid"
                hint="Boşsa misafir sipariş olarak açılır."
              >
                <input
                  id="mo-userid"
                  className="field font-mono text-xs"
                  value={form.userId ?? ''}
                  onChange={(event) => setForm((c) => ({ ...c, userId: event.target.value || null }))}
                />
              </Field>
            </div>
          </section>

          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Fiyat ve kargo</h2>

            <div className="space-y-4">
              <Field label="Kargo ücreti (elle)" htmlFor="mo-shipping">
                <input
                  id="mo-shipping"
                  type="number"
                  step="0.01"
                  min={0}
                  className="field"
                  value={form.shippingOverride ?? ''}
                  onChange={(event) =>
                    setForm((c) => ({
                      ...c,
                      shippingOverride: event.target.value ? Number(event.target.value) : null,
                    }))
                  }
                />
              </Field>

              <Field label="İndirim kodları" htmlFor="mo-codes" hint="Virgülle ayırın.">
                <input
                  id="mo-codes"
                  className="field font-mono text-sm uppercase"
                  value={form.discountCodes.join(', ')}
                  onChange={(event) =>
                    setForm((c) => ({
                      ...c,
                      discountCodes: event.target.value
                        .split(',')
                        .map((code) => code.trim().toUpperCase())
                        .filter(Boolean),
                    }))
                  }
                />
              </Field>
            </div>
          </section>

          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Notlar ve seçenekler</h2>

            <div className="space-y-4">
              <Field label="Müşteri notu" htmlFor="mo-cnote">
                <textarea
                  id="mo-cnote"
                  className="field min-h-20"
                  value={form.customerNote ?? ''}
                  onChange={(event) => setForm((c) => ({ ...c, customerNote: event.target.value }))}
                />
              </Field>

              <Field label="Personel notu" htmlFor="mo-snote">
                <textarea
                  id="mo-snote"
                  className="field min-h-20"
                  value={form.staffNote ?? ''}
                  onChange={(event) => setForm((c) => ({ ...c, staffNote: event.target.value }))}
                />
              </Field>

              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={form.saveAsDraft}
                  onChange={(event) => setForm((c) => ({ ...c, saveAsDraft: event.target.checked }))}
                />
                Taslak olarak kaydet (ödeme beklenmez)
              </label>

              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={form.sendPaymentLink}
                  onChange={(event) =>
                    setForm((c) => ({ ...c, sendPaymentLink: event.target.checked }))
                  }
                />
                Müşteriye ödeme bağlantısı gönder
              </label>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

/**
 * Urunun varyantlarini acilir listede gosterip secim yaptirir.
 *
 * Varyant ve stok bilgisi stok ucundan okunur; urun duzenleme ucu
 * form sozlesmesini dondurdugu icin satilabilir miktari tasimaz.
 */
function VariantPicker({
  productId,
  onPick,
}: {
  productId: number;
  onPick: (variantId: number) => void;
}) {
  const [open, setOpen] = useState(false);

  const inventory = useQuery({
    queryKey: ['admin', 'inventory', 'by-product', productId],
    queryFn: () => adminInventoryApi.search({ productId, pageSize: 100 }),
    enabled: open,
  });

  if (!open) {
    return (
      <button type="button" className="btn-secondary btn-sm shrink-0" onClick={() => setOpen(true)}>
        Varyant seç
      </button>
    );
  }

  return (
    <select
      className="field w-auto min-w-48 py-1.5 text-sm"
      aria-label="Varyant"
      defaultValue=""
      onChange={(event) => {
        if (event.target.value) {
          onPick(Number(event.target.value));
          setOpen(false);
        }
      }}
    >
      <option value="">{inventory.isLoading ? 'Yükleniyor…' : 'Varyant seçin'}</option>
      {(inventory.data?.items ?? []).map((item) => (
        <option
          key={item.productVariantId}
          value={item.productVariantId}
          disabled={item.totalAvailable <= 0}
        >
          {item.variantTitle} · {item.sku} · satılabilir {item.totalAvailable}
        </option>
      ))}
    </select>
  );
}
